import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import useFieldEvents from '@core/hooks/cohesion/useFieldEvents';
import { setDcsValue } from '@core/reducers/dcsSlice';
import { selectFormCorrelationId, selectStepContext } from '@core/reducers/eventingSlice';
import { VoyagerInput, selectInputObject, setInput } from '@core/reducers/inputsSlice';

import { Option } from '../types';
import type { TOptionGroupField } from './types';

export const isSelected = (selection: VoyagerInput, option: Option) => {
  const selectedOption = selection?.options as unknown as Option;
  if (!selectedOption?.value && !selectedOption?.label) return false;
  return selectedOption.value === option.value && selectedOption.label === option.label;
};

const SingleOptionGroupField: TOptionGroupField = ({ field, heading, InputComponent, fieldNumber }) => {
  // Action Dispatcher
  const dispatch = useDispatch();

  // Form Correlation Id for events
  const formCorrelationId = useSelector(selectFormCorrelationId);
  // stepContext for for fields events
  const stepContext = useSelector(selectStepContext);

  const { fieldSelected, fieldViewed } = useFieldEvents({
    correlationId: formCorrelationId,
    userInputField: {
      autofilled: false,
      fieldName: field.name,
      fieldLabel: heading,
      fieldType: field.type,
      fieldNumber,
    },
  });

  useEffect(() => {
    fieldViewed();
  }, []);

  // Inputs State properties
  const fieldInputObject = useSelector((state) => selectInputObject(state, field.name as string));

  const handleSelection = useCallback(
    (option: Option) => {
      fieldSelected({
        userInputField: {
          fieldValue: option.label,
        },
      });

      if (isSelected(fieldInputObject, option)) {
        dispatch(setInput({ key: field.name as string, value: undefined, options: undefined }));
      } else {
        if (field.dcsInput) {
          dispatch(setDcsValue({ key: field.dcsInput, value: option.value }));
        }
        dispatch(setInput({ key: field.name as string, value: option.value, options: option }));
      }
    },
    [fieldInputObject, field, heading, isSelected, stepContext]
  );

  return (
    <InputComponent
      field={field}
      selection={fieldInputObject}
      handleSelection={handleSelection}
      isSelected={isSelected}
    />
  );
};

export default SingleOptionGroupField;
